import { addToCartAsync, updateCountItemsInCart } from "@src/api/cart";
import { getPriceAsync } from "@src/api/price";
import { OrderInfo } from "@src/app/order-info-context";
import { CartModel } from "@src/app/types";
import PostcodeBox from "@src/components/form/PostcodeBox";
import IconWithTooltip from "@src/components/icon-with-tooltip/IconWithTooltip";
import IconWithScroll from "@src/components/icon-with-scroll/IconWithScroll";
import FullModal from "@src/components/modal/FullModal";
import useBreakpoint, { Breakpoint } from "@src/hooks/use-breakpoint";
import { lazy, useEffect, useRef } from "react";
import { useState } from "react";
import { getServiceAsync } from "../../api/service";
import Button from "../../components/button/Button";
import TrustBox, { TrustBoxProps } from "../../components/trustbox/TrustBox";
import MiniCart from "../mini-cart/MiniCart";
import { OrderFlowModel, StepType } from "../order-flow/OrderFlow";
import { gtmPushEventAddToCart } from "@src/helpers/gtm";

const OrderFlow = lazy(() => import("../order-flow/OrderFlow"));

interface ValidationMessages {
  invalidPostcode: string;
}

interface CtaTexts {
  continue: string;
  edit: string;
  addToCart: string;
}

interface Cart {
  addedToCartText: string;
  viewCartText: string;
  viewCartUrl: string;
  checkoutText: string;
  checkoutUrl: string;
  vatText: string;
  totalText: string;
}

interface Props {
  ctaTexts: CtaTexts;
  getQuoteMessage: string;
  loginMessage: string;
  paymentPeriod: string;
  postCodePattern: string;
  cart: Cart;
  postcodePlaceholder: string;
  postcodeTooltip: string;
  savedPostCode: string;
  productNotAvailableInRegion: string;
  serviceId: number;
  startingPrice: string;
  title: string;
  trustpilot: TrustBoxProps;
  validationMessages: ValidationMessages;
  requiredValidationText?: string;
}

let orderFlowKey = 1;

const ServiceShoppingForm = ({
  ctaTexts,
  getQuoteMessage,
  loginMessage,
  paymentPeriod,
  postcodePlaceholder,
  postcodeTooltip,
  savedPostCode,
  productNotAvailableInRegion,
  postCodePattern,
  serviceId,
  startingPrice,
  title,
  trustpilot,
  validationMessages,
  requiredValidationText,
  ...props
}: Props) => {
  const breakpoint = useBreakpoint();
  const [closeClicked, setCloseClicked] = useState(false);
  const [finalPrice, setFinalPrice] = useState<string>();
  const [totalPriceWithoutVAT, setTotalPriceWithoutVAT] = useState<string>();
  const [priceTooltip, setPriceTooltip] = useState<string>();
  const [postcode, setPostcode] = useState<string>();
  const [orderFlowModel, setOrderFlowModel] = useState<OrderFlowModel>();
  const [orderFlowVisible, setOrderFlowVisible] = useState<boolean>(false);
  const [orderInfo, setOrderInfo] = useState<OrderInfo>();
  const [isApiCalling, setIsApiCalling] = useState(false);
  const [cartInfo, setCartInfo] = useState<CartModel>();
  const [showReplicate, setShowReplicate] = useState(false);
  const [formSubmitTitle, setFormSubmitTitle] = useState<String>();
  const [formSubmitText, setFormSubmitText] = useState<String>();
  const [isValid, setIsValid] = useState<boolean>(true);
  const [errors, setErrors] = useState<string[]>([]);

  const [finalPaymentPeriod, setFinalPaymentPeriod] = useState<string | null>(paymentPeriod);

  useEffect(() => {
    if (breakpoint === Breakpoint.desktop) {
      setShowReplicate(false);
    }
  }, [breakpoint]);

  useEffect(() => {
    if (savedPostCode) {
      setPostcode(savedPostCode);
    }
  }, []);

  const handleValidatePostCode = (value?: string) => {
    let hasError = false;
    const errors: string[] = [];

    if (!value) {
      hasError = true;
      if (requiredValidationText) {
        errors.push(requiredValidationText);
      }
    }

    if (postCodePattern) {
      if (!value || !new RegExp(postCodePattern, "gi").test(value.trim())) {
        hasError = true;

        if (validationMessages.invalidPostcode) {
          errors.push(validationMessages.invalidPostcode);
        }
      }
    }

    return {
      isValid: !hasError,
      errors,
    };
  };

  const pushContinueClickEvent = () => {
    if (window.dataLayer)
      window.dataLayer.push({
        event: "postcodeEntered",
      });
  };

  const handleContinueClick = () => {
    const { isValid, errors } = handleValidatePostCode(postcode);
    setIsValid(isValid);
    setErrors(errors);

    if (!postcode || !isValid) {
      return;
    }

    pushContinueClickEvent();
    setIsApiCalling(true);
    getServiceAsync(postcode.trim(), serviceId)
      .then((data: OrderFlowModel) => {
        if (data.postcode && data.steps.constructor === Array && data.steps.length) {
          setOrderFlowModel(data);
          setOrderFlowVisible(true);
        }
      })
      .catch((error) => {
        console.log(error);
        setOrderFlowVisible(false);
      })
      .finally(() => setIsApiCalling(false));
  };

  const handlePostCodeChange = (value: string | undefined) => {
    setPostcode(value);
    setErrors([]);
    setIsValid(true);
  };

  const handleEditSelection = () => {
    if (orderFlowModel?.steps?.length) {
      const userGateIndex = orderFlowModel.steps.findIndex((s) => s.type == StepType.userGate);
      if (userGateIndex >= 0) {
        setOrderFlowModel({ ...orderFlowModel, steps: orderFlowModel.steps.filter((s) => s.type !== StepType.userGate) });
      }

      setOrderFlowVisible(true);
    }
  };

  const callGetPriceApiAsync = async (order: OrderInfo) => {
    const data = await getPriceAsync({ items: [order], paymentPeriod });
    setFinalPrice(data.totalPrice);
    if (data.paymentPeriod) setFinalPaymentPeriod(data.paymentPeriod);
    setTotalPriceWithoutVAT(data.notIncludeVatTotalPrice);
    setPriceTooltip(data.tooltip);
    setFormSubmitTitle(data.formSubmitTitle);
    setFormSubmitText(data.formSubmitText);
  };

  const callAddToCartApiAsync = async (order: OrderInfo) => {
    const data = await addToCartAsync({ items: [order] });
    const item = data.items[0];
    gtmPushEventAddToCart({
      name: item.name,
      variant: item.description,
      price: item.priceIncludedTax,
      quantity: item.quantity,
      id: item.productId,
    });
    updateCountItemsInCart();
    return data;
  };

  const handleOrderFlowClose = (orderInfo?: any) => {
    setOrderFlowVisible(false);

    if (orderInfo) {
      setFinalPrice(undefined);
      setOrderInfo(orderInfo);
      setIsApiCalling(true);
      const promises = [callAddToCartApiAsync(orderInfo), callGetPriceApiAsync(orderInfo)];
      Promise.all(promises)
        .then((values) => setCartInfo(values[0]))
        .catch((error) => {
          console.log(error);
        })
        .finally(() => setIsApiCalling(false));
    }
  };

  const handleAddToCart = () => {
    if (orderInfo) {
      setCartInfo(undefined);
      setIsApiCalling(true);
      callAddToCartApiAsync(orderInfo)
        .then((data) => setCartInfo(data as CartModel))
        .catch((error) => {
          console.log(error);
        })
        .finally(() => setIsApiCalling(false));
    }
  };

  const handleClose = () => {
    if (closeClicked) {
      setShowReplicate(false);
      setCloseClicked(false);
    } else {
      setCloseClicked(true);
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  (window as any).ref2 = ref;

  const handleCloseMinicart = () => {
    setCartInfo(undefined);
  };

  const handlePostcodeClick = (replicate?: boolean) => {
    if (!replicate && breakpoint != Breakpoint.desktop) {
      setShowReplicate(true);
    }
  };

  const disabled = isApiCalling;

  const classWithOptionalReplicateModifier = (baseClass: string, replicate?: boolean) => {
    let className = baseClass;

    if (replicate) {
      className += " " + baseClass + "--replicate";
    }

    if (finalPrice) {
      className += " " + baseClass + "--has-price";
    }

    return className;
  };

  const inner = (replicate?: boolean) => {
    return (
      <div className={classWithOptionalReplicateModifier("fm-o-postcode-form__container", replicate)}>
        <div className={classWithOptionalReplicateModifier("fm-o-postcode-form__inner", replicate)}>
          {savedPostCode && !finalPrice && !formSubmitTitle && (
            <>
              <h3 className="fm-o-postcode-form__title">{title}</h3>
              <h4 className="fm-o-postcode-form__rrp">{startingPrice}</h4>
              <div className="fm-m-postcode-box tfm fm-o-postcode-form__postcode">
                <div className="fm-o-postcode-form__postcode-current">{postcode}</div>
                {productNotAvailableInRegion && (
                  <div className="fm-o-postcode-form__product-not-available" dangerouslySetInnerHTML={{ __html: productNotAvailableInRegion }}></div>
                )}
                {!productNotAvailableInRegion && (
                  <Button
                    text={isApiCalling ? "Loading..." : ctaTexts.continue}
                    onClick={handleContinueClick}
                    mainClass={"fm-o-postcode-form__continue-btn" + (isApiCalling ? " fm-o-postcode-form__continue-btn--loading" : "")}
                    styleModifier={["fm-a-button--yellow"]}
                  />
                )}
              </div>
              <div className="fm-o-postcode-form__extra">
                <div
                  className="fm-o-postcode-form__extra-col fm-o-postcode-form__extra-col-1"
                  dangerouslySetInnerHTML={{ __html: getQuoteMessage }}
                ></div>
                <div
                  className="fm-o-postcode-form__extra-col fm-o-postcode-form__extra-col-2"
                  dangerouslySetInnerHTML={{ __html: loginMessage }}
                ></div>
              </div>
            </>
          )}

          {!finalPrice && !formSubmitTitle && !savedPostCode && (
            <>
              <h3 className="fm-o-postcode-form__title">{title}</h3>
              <h4 className="fm-o-postcode-form__rrp">{startingPrice}</h4>

              <PostcodeBox
                mainClass="fm-o-postcode-form__postcode fm-m-postcode-box--light"
                disabled={isApiCalling || (!replicate && showReplicate)}
                maxLength={15}
                pattern={postCodePattern}
                patternValidationText={validationMessages.invalidPostcode}
                placeholder={postcodePlaceholder}
                tooltip={postcodeTooltip}
                value={postcode}
                onClick={() => handlePostcodeClick(replicate)}
                onChange={handlePostCodeChange}
                isValid={isValid}
                errors={errors}
                ref={replicate ? ref : undefined}
              />

              {(breakpoint == Breakpoint.desktop || replicate) && (
                <Button
                  text={isApiCalling ? "Loading..." : ctaTexts.continue}
                  onClick={handleContinueClick}
                  mainClass={"fm-o-postcode-form__continue-btn" + (isApiCalling ? " fm-o-postcode-form__continue-btn--loading" : "")}
                  styleModifier={["fm-a-button--yellow"]}
                />
              )}

              <div className="fm-o-postcode-form__extra">
                <div
                  className="fm-o-postcode-form__extra-col fm-o-postcode-form__extra-col-1"
                  dangerouslySetInnerHTML={{ __html: getQuoteMessage }}
                ></div>
                <div
                  className="fm-o-postcode-form__extra-col fm-o-postcode-form__extra-col-2"
                  dangerouslySetInnerHTML={{ __html: loginMessage }}
                ></div>
              </div>

              {!replicate && breakpoint != Breakpoint.desktop && (
                <div className="fm-o-postcode-form__scroll-arrow-container">
                  <IconWithScroll mainClass="fm-o-postcode-form__scroll-arrow" target="#fm-o-service-variation-summary" />
                </div>
              )}
            </>
          )}
          {finalPrice && (
            <>
              <div className="fm-o-postcode-form__price-wrapper">
                <h3 className="fm-o-postcode-form__price">{totalPriceWithoutVAT}</h3>
                {priceTooltip && <IconWithTooltip mainClass="fm-o-postcode-form__price-tooltip" text={priceTooltip} />}
              </div>
              <h4 className="fm-o-postcode-form__payment-period">{finalPaymentPeriod}</h4>
              <Button
                text={isApiCalling ? "Loading..." : ctaTexts.addToCart}
                onClick={handleAddToCart}
                disabled={disabled}
                mainClass={"fm-o-postcode-form__add-to-cart-btn" + (isApiCalling ? " fm-o-postcode-form__continue-btn--loading" : "")}
                styleModifier={["fm-a-button--yellow"]}
              />
              <Button
                text={ctaTexts.edit}
                onClick={handleEditSelection}
                disabled={disabled}
                mainClass="fm-o-postcode-form__edit-btn"
                styleModifier={["fm-a-button--light-outline"]}
                icon={{ iconPath: "/assets/images/new-icons.svg#edit", viewBoxWidth: 22, viewBoxHeight: 23, width: 20, height: 20 }}
              />
            </>
          )}

          {formSubmitTitle && (
            <>
              <div className="fm-o-postcode-form__form-submit-message">
                <h2 className="fm-o-postcode-form__form-submit-title">{formSubmitTitle}</h2>
                {formSubmitText && <h5 className="fm-o-postcode-form__form-submit-text">{formSubmitText}</h5>}
              </div>
              <Button
                text={ctaTexts.edit}
                onClick={handleEditSelection}
                disabled={disabled}
                mainClass="fm-o-postcode-form__edit-btn"
                styleModifier={["fm-a-button--light-outline"]}
                icon={{ iconPath: "/assets/images/new-icons.svg#edit", viewBoxWidth: 22, viewBoxHeight: 23, width: 20, height: 20 }}
              />
            </>
          )}

          {breakpoint === Breakpoint.desktop && (
            <div className="fm-o-postcode-form__trustpilot">
              <TrustBox {...trustpilot} />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {inner()}
      {breakpoint !== Breakpoint.desktop && showReplicate && (
        <FullModal
          closeClicked={closeClicked}
          darkMode={true}
          onClose={handleClose}
          iconPath={"#close-icon-secondary"}
          animationCallbackOverride={(e) => {
            if (closeClicked) {
              handleClose();
            } else {
              if (showReplicate) {
                if (ref.current) {
                  ref.current.focus();
                }
              }
            }
          }}
          handleTouchMove={() => {
            if (ref.current && ref.current === document.activeElement) {
              ref.current.blur();
            }
          }}
        >
          {inner(true)}
        </FullModal>
      )}
      {orderFlowModel && orderFlowVisible && (
        <OrderFlow
          key={++orderFlowKey}
          initial={orderInfo}
          serviceId={orderFlowModel.serviceId}
          postcode={orderFlowModel.postcode}
          steps={orderFlowModel.steps}
          onOrderFlowClose={handleOrderFlowClose}
        />
      )}
      {cartInfo && <MiniCart key={++orderFlowKey} {...{ ...cartInfo, ...props.cart, onClose: handleCloseMinicart }} />}
    </>
  );
};

export default ServiceShoppingForm;
